import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, ArrowDownTrayIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PainelIris, inputIris, btnSecundario, btnDanger } from '../ui/iris';
import { logsApi } from '../../api/logs';
import { baixarBlob } from '../../utils/baixarBlob';
import { descreverLimpezaLogs } from '../../utils/escopoLogs';
import { useToast } from '../ui/Toast';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import type { LogEvento, LogFilters } from '../../types/logs';

const NIVEIS = ['Warning', 'Error', 'Fatal'];

// rose é palette padrão do Tailwind (alpha /15 funciona); warning/danger são
// tokens Íris (color-mix) — NUNCA usar /N neles (Tailwind descarta em silêncio).
const corNivel = (level: string): string => {
  switch (level) {
    case 'Fatal': return 'bg-danger-soft text-danger';
    case 'Error': return 'bg-rose-500/15 text-rose-600 dark:text-rose-300';
    case 'Warning': return 'bg-warning-soft text-warning';
    default: return 'bg-surface-2 text-tx-2';
  }
};

const fmt = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
};

const prettyJson = (s: string): string => {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
};

/**
 * Logs técnicos (Serilog Warning+) dentro da plataforma — diagnóstico sem SSH
 * no servidor. Distinto da Auditoria (evento de negócio): aqui é a falha técnica
 * (exceção, stack, source). Cruzam pelo Correlation ID.
 */
const AbaLogs: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [level, setLevel] = useState('');
  const [dias, setDias] = useState<number>(7);
  const [q, setQ] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<LogEvento[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [limpando, setLimpando] = useState(false);
  const [expandido, setExpandido] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await logsApi.listar(
        { level: level || undefined, dias, q: q || undefined, correlationId: correlationId || undefined },
        page, 50,
      );
      setLogs(r.data);
      setTotalPages(r.pagination.totalPages);
      setTotalItems(r.pagination.totalItems);
    } catch {
      setLogs([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setCarregando(false);
    }
  }, [level, dias, q, correlationId, page]);

  useEffect(() => { carregar(); }, [carregar]);

  // Baixa os logs dos filtros atuais como JSON (pra anexar/mandar analisar).
  const exportar = async () => {
    setExportando(true);
    try {
      const blob = await logsApi.exportar({ level: level || undefined, dias, q: q || undefined, correlationId: correlationId || undefined });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      baixarBlob(`utilyhub-logs-${stamp}.json`, blob);
    } catch {
      toast.erro('Não foi possível exportar os logs.');
    } finally {
      setExportando(false);
    }
  };

  // Mudar filtro volta pra página 1 (evita ficar numa página que não existe mais).
  const reset = () => setPage(1);

  // Apaga o que a listagem mostra (MESMOS filtros). Pedido do suporte: depois de
  // corrigir, o ruído antigo esconde o que ainda falha. O texto do diálogo diz o
  // escopo exato — sem filtro é a tabela inteira, e quem clica precisa saber.
  const limpar = async () => {
    const filtros: LogFilters = { level: level || undefined, dias, q: q || undefined, correlationId: correlationId || undefined };
    const ok = await confirm({
      titulo: 'Apagar logs técnicos?',
      mensagem: `${descreverLimpezaLogs(filtros, totalItems)} A auditoria não é afetada. Esta ação não pode ser desfeita.`,
      textoConfirmar: 'Apagar',
      destrutivo: true,
    });
    if (!ok) return;
    setLimpando(true);
    try {
      const n = await logsApi.limpar(filtros);
      toast.sucesso(`${n} evento(s) de log apagado(s).`);
      reset();
      await carregar();
    } catch {
      toast.erro('Não foi possível apagar os logs.');
    } finally {
      setLimpando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PainelIris
        titulo="Logs técnicos"
        descricao="Avisos, erros e falhas do servidor (Warning+). Para diagnosticar problemas sem acessar a máquina. Mensagens de rotina (Info) ficam só no arquivo."
        acao={
          // Ações no cabeçalho do painel, não na linha de filtros: lá elas
          // quebravam pra uma 2ª linha sozinhas. Limpar (destrutivo) fica
          // separado do par de leitura por um divisor, não só pela cor.
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button onClick={() => { reset(); carregar(); }} disabled={carregando} className={`${btnSecundario} h-11 sm:h-9 px-3 text-xs`}>
              <ArrowPathIcon className={`w-3.5 h-3.5 ${carregando ? 'animate-spin' : ''}`} /> Atualizar
            </button>
            <button
              onClick={exportar}
              disabled={exportando || totalItems === 0}
              title="Baixar os logs dos filtros atuais como arquivo JSON (até 5000)"
              className={`${btnSecundario} h-11 sm:h-9 px-3 text-xs`}
            >
              <ArrowDownTrayIcon className={`w-3.5 h-3.5 ${exportando ? 'animate-pulse' : ''}`} /> {exportando ? 'Exportando…' : 'Exportar'}
            </button>
            <span aria-hidden className="mx-1 hidden h-6 w-px bg-line sm:block" />
            <button
              onClick={limpar}
              disabled={limpando || carregando || totalItems === 0}
              title="Apagar os logs dos filtros atuais (sem filtro, apaga tudo). Use depois de corrigir um problema, pra tirar o ruído antigo."
              className={`${btnDanger} h-11 sm:h-9 px-3 text-xs`}
            >
              <TrashIcon className={`w-3.5 h-3.5 ${limpando ? 'animate-pulse' : ''}`} /> {limpando ? 'Apagando…' : 'Limpar'}
            </button>
          </div>
        }
      >
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-tx-3 mb-1">Nível</label>
            <select value={level} onChange={e => { setLevel(e.target.value); reset(); }} className={`w-32 ${inputIris}`}>
              <option value="">Todos</option>
              {NIVEIS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-tx-3 mb-1">Período</label>
            <select value={dias} onChange={e => { setDias(Number(e.target.value)); reset(); }} className={`w-32 ${inputIris}`}>
              <option value={1}>24 horas</option>
              <option value={7}>7 dias</option>
              <option value={30}>30 dias</option>
              <option value={0}>Tudo</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-wide text-tx-3 mb-1">Busca (mensagem / source / exceção)</label>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { reset(); carregar(); } }}
              placeholder="ex.: certificado, timeout, SEFAZ…"
              className={`w-full ${inputIris}`}
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-tx-3 mb-1">Correlation ID</label>
            <input
              value={correlationId}
              onChange={e => { setCorrelationId(e.target.value.trim()); reset(); }}
              placeholder="32 hex"
              className={`w-40 font-mono ${inputIris}`}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-tx-3 border-b border-line">
                <th className="py-2 px-2 w-8"></th>
                <th className="py-2 px-2">Quando</th>
                <th className="py-2 px-2">Nível</th>
                <th className="py-2 px-2">Source</th>
                <th className="py-2 px-2">Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && !carregando && (
                <tr><td colSpan={5} className="py-8 text-center text-tx-3">Nenhum log no período/filtro.</td></tr>
              )}
              {logs.map(l => (
                <React.Fragment key={l.id}>
                  <tr
                    className="border-b border-line hover:bg-surface-2 transition-colors cursor-pointer"
                    onClick={() => setExpandido(expandido === l.id ? null : l.id)}
                  >
                    <td className="py-1.5 px-2 text-tx-3">
                      <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform ${expandido === l.id ? 'rotate-90' : ''}`} />
                    </td>
                    <td className="py-1.5 px-2 whitespace-nowrap font-mono text-[11px] text-tx-2">{fmt(l.criadoEm)}</td>
                    <td className="py-1.5 px-2">
                      <span className={`inline-block rounded-iris-sm px-2 py-0.5 text-[10px] font-semibold ${corNivel(l.level)}`}>{l.level}</span>
                    </td>
                    <td className="py-1.5 px-2 text-tx-2 font-mono text-[11px] max-w-[180px] truncate" title={l.sourceContext ?? ''}>
                      {l.sourceContext?.split('.').pop() ?? '—'}
                    </td>
                    <td className="py-1.5 px-2 text-tx max-w-[440px] truncate" title={l.message ?? ''}>{l.message ?? '—'}</td>
                  </tr>
                  {expandido === l.id && (
                    <tr className="bg-surface-2">
                      <td></td>
                      <td colSpan={4} className="py-3 px-2">
                        <div className="space-y-2 text-xs">
                          <div><span className="text-tx-3">Mensagem: </span><span className="text-tx">{l.message}</span></div>
                          {l.sourceContext && <div><span className="text-tx-3">Source: </span><span className="font-mono text-tx-2">{l.sourceContext}</span></div>}
                          {l.correlationId && <div><span className="text-tx-3">Correlation: </span><span className="font-mono text-tx-2">{l.correlationId}</span></div>}
                          {l.maquina && <div><span className="text-tx-3">Máquina: </span><span className="text-tx-2">{l.maquina}</span></div>}
                          {l.propriedades && (
                            <div>
                              <span className="text-tx-3">Propriedades:</span>
                              <pre className="mt-1 p-2 rounded-iris bg-surface border border-line overflow-x-auto text-[11px] text-tx-2">{prettyJson(l.propriedades)}</pre>
                            </div>
                          )}
                          {l.exception && (
                            <div>
                              <span className="text-tx-3">Exceção:</span>
                              <pre className="mt-1 p-2 rounded-iris bg-surface border border-line overflow-x-auto text-[11px] text-tx-2 whitespace-pre-wrap">{l.exception}</pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-tx-3">
          <span>{totalItems} evento(s)</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || carregando} className={`${btnSecundario} h-7 px-2 text-xs`}>Anterior</button>
            <span>Página {page} de {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || carregando} className={`${btnSecundario} h-7 px-2 text-xs`}>Próxima</button>
          </div>
        </div>
      </PainelIris>
    </div>
  );
};

export default AbaLogs;
